/**
 * Utilitaires de manipulation de dates pour DooDates
 *
 * RÈGLE CRITIQUE: Ne jamais utiliser toISOString() pour convertir des dates locales
 * en string YYYY-MM-DD car toISOString() retourne en UTC.
 *
 * En France (UTC+2), new Date(2025, 9, 25).toISOString() retourne "2025-10-24T22:00:00Z"
 * ce qui cause un décalage d'un jour.
 */

/**
 * Convertit une locale app ("fr" | "en" | "es") en tag BCP-47 pour Intl.
 * Centralisé ici pour éviter les "fr-FR" hardcodés à travers le codebase.
 */
export function toIntlLocale(locale: string = "fr"): string {
  if (locale.startsWith("en")) return "en-US";
  if (locale.startsWith("es")) return "es-ES";
  return "fr-FR";
}

/** Premier jour de la semaine : 0 = dimanche, 1 = lundi. */
export type WeekStart = 0 | 1;

/**
 * Déduit le premier jour de la semaine selon la locale via Intl.Locale.weekInfo.
 * en-US → dimanche (0) ; fr-FR / es-ES et la plupart de l'Europe → lundi (1).
 * Fallback lundi si l'API n'est pas disponible (vieux Safari).
 *
 * @param locale - Tag BCP-47 (ex: "en-US") ou locale app ("en")
 */
export function getLocaleWeekStart(locale: string = "fr"): WeekStart {
  try {
    const tag = locale.includes("-") ? locale : toIntlLocale(locale);
    const loc = new Intl.Locale(tag) as Intl.Locale & {
      weekInfo?: { firstDay?: number };
      getWeekInfo?: () => { firstDay?: number };
    };
    // weekInfo.firstDay : 1 = lundi … 7 = dimanche (ISO).
    const info =
      typeof loc.getWeekInfo === "function" ? loc.getWeekInfo() : loc.weekInfo;
    return info?.firstDay === 7 ? 0 : 1;
  } catch {
    return 1;
  }
}

/**
 * Formate une date au format complet uniforme : "Samedi 15 novembre 2025"
 * Format standardisé pour l'affichage des dates dans toute l'application
 *
 * @param dateString - Date au format YYYY-MM-DD
 * @param locale - Locale app ("fr" par défaut, "en" pour en-US)
 * @returns String formatée selon la locale (ex: "Samedi 15 novembre 2025" / "Saturday, November 15, 2025")
 *
 * @example
 * formatDateFull("2025-11-15"); // "samedi 15 novembre 2025"
 * formatDateFull("2025-11-15", "en"); // "Saturday, November 15, 2025"
 */
export function formatDateFull(
  dateString: string,
  locale: string = "fr",
): string {
  // Parser la date en mode local pour éviter les décalages timezone
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day); // month - 1 car JS commence à 0

  return date.toLocaleDateString(toIntlLocale(locale), {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Formate un nombre selon la locale app (séparateurs locaux).
 * @example formatNumber(1234.5, "fr") // "1 234,5" ; formatNumber(1234.5, "en") // "1,234.5"
 */
export function formatNumber(value: number, locale: string = "fr"): string {
  return value.toLocaleString(toIntlLocale(locale));
}

/**
 * Formate un montant monétaire selon la locale app.
 * en → USD ($), fr → EUR (€). La devise réelle de facturation est gérée par
 * LemonSqueezy (Merchant of Record) au checkout.
 */
export function formatCurrency(value: number, locale: string = "fr"): string {
  const isEn = locale.startsWith("en");
  return value.toLocaleString(toIntlLocale(locale), {
    style: "currency",
    currency: isEn ? "USD" : "EUR",
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
  });
}

/**
 * Convertit une Date en string YYYY-MM-DD en utilisant l'heure locale
 * (pas UTC comme toISOString)
 *
 * @param date - La date à convertir
 * @returns String au format "YYYY-MM-DD" en heure locale
 *
 * @example
 * const date = new Date(2025, 9, 25); // 25 octobre 2025
 * formatDateLocal(date); // "2025-10-25" (pas "2025-10-24"!)
 */
export function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Retourne la date d'aujourd'hui au format YYYY-MM-DD en heure locale
 *
 * @returns String "YYYY-MM-DD" pour aujourd'hui
 */
export function getTodayLocal(): string {
  return formatDateLocal(new Date());
}

/**
 * Filtre un tableau de dates pour ne garder que les dates futures ou aujourd'hui
 *
 * @param dates - Array de strings YYYY-MM-DD
 * @param referenceDate - Date de référence (optionnel, par défaut aujourd'hui)
 * @returns Array filtré avec seulement les dates >= référence
 */
export function filterFutureDates(
  dates: string[],
  referenceDate?: string,
): string[] {
  const today = referenceDate || getTodayLocal();
  return dates.filter((date) => date >= today);
}

/**
 * Type pour représenter un groupe de dates consécutives
 */
export interface DateGroup {
  dates: string[]; // Liste des dates YYYY-MM-DD du groupe
  label: string; // Label à afficher ("Week-end du 6-7 décembre", "Semaine du 2 au 8 décembre")
  type: "weekend" | "week" | "fortnight" | "custom"; // Type de groupe
}

/**
 * Détecte et groupe les dates consécutives en périodes significatives
 * (semaines complètes, quinzaines)
 * Les week-ends et autres périodes de moins de 7 jours restent individuels
 * sauf si allowWeekendGrouping est true (pour les suggestions du chat)
 *
 * @param dates - Array de dates YYYY-MM-DD triées
 * @param allowWeekendGrouping - Si true, permet de regrouper les samedi-dimanche consécutifs (pour suggestions chat)
 * @returns Array de DateGroup
 *
 * @example
 * const dates = ["2025-12-06", "2025-12-07", "2025-12-08", "2025-12-09", "2025-12-10", "2025-12-11", "2025-12-12"];
 * groupConsecutiveDates(dates);
 * // [
 * //   { dates: [...], label: "Semaine du 6 au 12 décembre", type: "week" }
 * // ]
 */
export function groupConsecutiveDates(
  dates: string[],
  allowWeekendGrouping: boolean = false,
): DateGroup[] {
  if (!Array.isArray(dates) || dates.length === 0) return [];

  // Filtrer les entrées invalides (null/undefined/non-string/vide) avant tout
  // traitement : la source des dates peut être une suggestion IA mal formée.
  // Sans ce garde, une entrée falsy se propage jusqu'au formatage et produit
  // des labels "Invalid Date" (voire des accès sur `undefined`).
  const validDates = dates.filter(
    (d): d is string => typeof d === "string" && d.trim().length > 0,
  );
  if (validDates.length === 0) return [];

  // Trier les dates
  const sortedDates = [...validDates].sort();
  const groups: DateGroup[] = [];
  let currentGroup: string[] = [sortedDates[0]];

  // Grouper les dates consécutives
  for (let i = 1; i < sortedDates.length; i++) {
    const prevDate = new Date(sortedDates[i - 1]);
    const currDate = new Date(sortedDates[i]);
    const dayDiff =
      (currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24);

    if (dayDiff === 1) {
      // Date consécutive
      currentGroup.push(sortedDates[i]);
    } else {
      // Rupture de séquence, créer un groupe
      const result = createDateGroup(currentGroup, allowWeekendGrouping);
      // Si createDateGroup retourne un tableau (dates non groupées), l'aplatir
      if (Array.isArray(result)) {
        groups.push(...result);
      } else {
        groups.push(result);
      }
      currentGroup = [sortedDates[i]];
    }
  }

  // Ajouter le dernier groupe
  if (currentGroup.length > 0) {
    const result = createDateGroup(currentGroup, allowWeekendGrouping);
    // Si createDateGroup retourne un tableau (dates non groupées), l'aplatir
    if (Array.isArray(result)) {
      groups.push(...result);
    } else {
      groups.push(result);
    }
  }

  return groups;
}

/**
 * Crée un DateGroup à partir d'une liste de dates consécutives
 * IMPORTANT : Ne groupe que les périodes significatives (semaines complètes, quinzaines)
 * Les autres dates consécutives sont laissées individuelles
 * Sauf si allowWeekendGrouping est true : dans ce cas, les week-ends (samedi-dimanche) sont aussi groupés
 */
function createDateGroup(
  dates: string[],
  allowWeekendGrouping: boolean = false,
): DateGroup {
  const count = dates.length;
  const firstDate = new Date(dates[0]);
  const lastDate = new Date(dates[count - 1]);

  // Formater les dates pour l'affichage
  const firstDay = firstDate.getDate();
  const lastDay = lastDate.getDate();
  const firstMonth = firstDate.toLocaleDateString("fr-FR", { month: "long" });
  const lastMonth = lastDate.toLocaleDateString("fr-FR", { month: "long" });
  const year = firstDate.getFullYear();

  // Déterminer le type et le label
  if (
    count === 2 &&
    allowWeekendGrouping &&
    firstDate.getDay() === 6 &&
    lastDate.getDay() === 0
  ) {
    // Week-end (samedi-dimanche) - GROUPER uniquement si explicitement autorisé (suggestions chat)
    return {
      dates,
      label: `Week-end du ${firstDay}-${lastDay} ${firstMonth} ${year}`,
      type: "weekend",
    };
  } else if (count === 7) {
    // Semaine complète - GROUPER
    const sameMonth = firstMonth === lastMonth;
    const monthStr = sameMonth ? firstMonth : `${firstMonth}-${lastMonth}`;
    return {
      dates,
      label: `Semaine du ${firstDay} au ${lastDay} ${monthStr} ${year}`,
      type: "week",
    };
  } else if (count >= 14 && count <= 15) {
    // Quinzaine - GROUPER
    const sameMonth = firstMonth === lastMonth;
    const monthStr = sameMonth ? firstMonth : `${firstMonth}-${lastMonth}`;
    return {
      dates,
      label: `Quinzaine du ${firstDay} au ${lastDay} ${monthStr} ${year}`,
      type: "fortnight",
    };
  } else {
    // IMPORTANT : Pour 2-6 jours ou 8-13 jours, NE PAS GROUPER
    // Retourner chaque date individuellement
    // Cela évite de grouper "vendredi + samedi" ou "3 jours consécutifs"
    return dates.map((date) => {
      const singleDate = new Date(date);
      const dayName = singleDate.toLocaleDateString("fr-FR", {
        weekday: "long",
      });
      const day = singleDate.getDate();
      const month = singleDate.toLocaleDateString("fr-FR", { month: "long" });
      const year = singleDate.getFullYear();

      return {
        dates: [date],
        label: `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${day} ${month} ${year}`,
        type: "custom" as const,
      };
    }) as unknown as DateGroup; // Retourne un tableau, sera flatté par le parent
  }
}
